import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { endpoints } from "../../../../../core/defaultValues";
import { clientQuery } from "../../../../../core/data/GraphQLApi";
import { resetALL } from "../../../../../.globals/redux/actions";
import { ExceptionManager } from "../../../../../core/logManager";

const INIT_STATE = {
  loadingWorkOrders: false,
  workOrders: [],
  totalWorkOrders: 0,
  workOrdersGrouped: [],
  totalWorkOrdersGrouped: 0,
  loadingWorkOrdersGrouped: false,
  error: null,
};

const route = "modules/workOrders/views/main/components/trackerTab/redux/slice.";

const manageSearchWorkOrders = createAsyncThunk(
  "workOrders/trackerTab/manageSearchWorkOrders",
  async ({ conditions, sort, limit = 10, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await clientQuery(
        manageSearchWorkOrders.query,
        {
          conditions,
          sort,
          limit: limit || 10,
          offset: offset > -1 ? offset : 0,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );
      return response?.data || { items: [], total: 0 };
    } catch (exc) {
      ExceptionManager(exc, route, "searchWorkOrders");
      return rejectWithValue(exc);
    }
  }
);

manageSearchWorkOrders.query = `
    query ($conditions: [WorkOrderSearchInput], $sort: [WorkOrderSortInput], $limit: Int, $offset: Int) {
        data: searchWorkOrders(
            conditions: $conditions
            sort: $sort
            limit: $limit
            offset: $offset
        ) {
            total
            items {
                id
                customerId
                customer {
                  id
                  name
                }
                customerLocationId
                customerLocation {
                  id
                  name
                  address
                  latitude
                  longitude
                }
                workOrderTypeId
                workOrderType {
                  id
                  name
                }
                workOrderNumber
                description
                driverId
                driver {
                  id
                  title
                  avatarFile {
                    id
                    repositoryId
                    repositoryType
                    repositoryCode
                    repositoryPath
                    fileKey
                    originalName
                  }
                }
                scheduledDate
                closedDate
                estimatedDurationMins
                severity
                statusId
                status {
                  id
                  name
                  color
                }
                statusUpdatedOn
                dynamicFormAnswerId
                arrivalTime
                departureTime
            }
        }
    }
`;

export { manageSearchWorkOrders };

export const manageGetWorkOrders = createAsyncThunk("workOrders/trackerTab/manageGetWorkOrders", async (payload, { rejectWithValue }) => {
  try {
    const response = await clientQuery(
      `
        query ($id: Int!) {
            data: getWorkOrder(
                id: $id
            ) {
                id
                customerId
                customer {
                  id
                  name
                }
                customerLocationId
                customerLocation {
                  id
                  name
                  address
                  latitude
                  longitude
                }
                workOrderTypeId
                workOrderType {
                  id
                  name
                }
                workOrderNumber
                description
                driverId
                driver {
                  id
                  title
                  avatarFile {
                    id
                    repositoryId
                    repositoryType
                    repositoryCode
                    repositoryPath
                    fileKey
                    originalName
                  }
                }
                scheduledDate
                closedDate
                estimatedDurationMins
                severity
                statusId
                status {
                  id
                  name
                  color
                }
                statusUpdatedOn
            }
        }
    `,
      payload,
      endpoints.GRAPHQL_GENERAL_V2
    );

    return response?.data

  } catch (exc) {
    ExceptionManager(exc, route, "getWorkOrder");
    return rejectWithValue(exc);
  }
}
);

export const updateWorkOrderMainItem = createAsyncThunk("workOrders/trackerTab/updateWorkOrderMainItem", async (payload, { rejectWithValue, dispatch, getState }) => {
  const { id, key, items = [], total = 0 } = payload;
  if (key === "UPDATE") {
    const response = await clientQuery(
      `
        query ($id: Int!) {
            data: getWorkOrder(
                id: $id
            ) {
                id
                customerId
                customer {
                  id
                  name
                }
                customerLocationId
                customerLocation {
                  id
                  name
                  address
                  latitude
                  longitude
                }
                workOrderTypeId
                workOrderType {
                  id
                  name
                }
                workOrderNumber
                description
                driverId
                driver {
                  id
                  title
                  avatarFile {
                    id
                    repositoryId
                    repositoryType
                    repositoryCode
                    repositoryPath
                    fileKey
                    originalName
                  }
                }
                scheduledDate
                closedDate
                estimatedDurationMins
                severity
                statusId
                status {
                  id
                  name
                  color
                }
                statusUpdatedOn
                dynamicFormAnswerId
            }
        }
      `,
      {
        id: id
      },
      endpoints.GRAPHQL_GENERAL_V2
    );
    if (response?.data) {
      // Map the getWorkOrder response to match the searchWorkOrderGrouped structure
      const workOrderData = response.data;
      const cloneResponse = {
        __typename: "WorkOrderGrouped",
        recordType: "DETAIL",
        workOrderId: workOrderData.id,
        id: workOrderData.id,
        name: workOrderData.driver?.title || "",
        unitId: null,
        unitName: null,
        avatarFileId: workOrderData.driver?.avatarFile?.id,
        fileKey: workOrderData.driver?.avatarFile?.fileKey,
        repositoryPath: workOrderData.driver?.avatarFile?.repositoryPath,
        customerName: workOrderData.customer?.name || "",
        contactName: null,
        contactEmail: null,
        contactPhone: null,
        statusName: workOrderData.status?.name || "",
        statusColor: workOrderData.status?.color || "",
        durationInMinutes: workOrderData.estimatedDurationMins,
        workOrderTypeName: workOrderData.workOrderType?.name || "",
        workOrderAddress: workOrderData.customerLocation?.address || "",
        latitude: workOrderData.customerLocation?.latitude,
        longitude: workOrderData.customerLocation?.longitude,
        days: null,
        driverId: workOrderData.driverId,
        dynamicFormAnswerId: workOrderData.dynamicFormAnswerId,
        scheduledDate: workOrderData.scheduledDate,
        statusId: workOrderData.statusId,
        workOrderNumber: workOrderData.workOrderNumber,
        severity: workOrderData.severity,
        description: workOrderData.description,
        workOrderTypeId: workOrderData.workOrderTypeId,
        statusUpdatedOn: workOrderData.statusUpdatedOn
      };
      const updatedWorkOrders = items.map((item) =>
        item.id === workOrderData.id && item.recordType === "DETAIL" ? { ...item, ...cloneResponse } : item
      );
      return { items: updatedWorkOrders, total: total };
    }
    return items;
  } else if (key === "DELETE") {
    const exists = items.some(item => item.id === id);
    if (exists) {
      const updatedWorkOrders = items.filter((item) => item?.id !== id);
      return { items: updatedWorkOrders, total: total > 0 ? total - 1 : 0 };
    }
  }
  return { items, total };
})

export const searchWorkOrderGrouped = createAsyncThunk("workOrders/trackerTab/searchWorkOrderGrouped2", async (payload, { rejectWithValue }) => {
  const { conditions, limit = 20, offset = 0 } = payload;
  
  const dateRangeCondition = conditions.find(condition => condition.range);
  const searchCondition = conditions.find(condition => condition.terms);
  
  const { range = {}, type, timeZone, statusIds, severityIds, typeIds, driverIds, customerIds, tagIds, forceRefreshMemcached } = dateRangeCondition || {};
  const { terms = [] } = searchCondition || {};

  const openSearch = terms.join("").trim() || "";

  try {
      const response = await clientQuery(
        searchWorkOrderGrouped.query,
      {
        openSearch,
        dateRange: {
          startDate: range?.start || "",
          endDate: range?.end || ""
        },
        driverIds: driverIds || [],
        statusIds: statusIds || [],
        severityIds: severityIds|| [],
        typeIds: typeIds || [],
        customerIds: customerIds || [],
        tagIds: tagIds || [],
        type,
        allDrivers: driverIds?.length > 0 ? 
                    driverIds.includes(-99) ? true : false 
                    : tagIds?.length > 0 ? tagIds.includes(-99) ? true : false : true,
        timezone: timeZone,
        limit,
        offset,
        sort: payload?.sort || [{ field: "workOrderNumber", order: "ASC" }],
        forceRefreshMemcached: forceRefreshMemcached || false
      },
      endpoints.GRAPHQL_GENERAL_V2
    );
    return response?.data || { items: [], total: 0 };
  } catch (exc) {
    ExceptionManager(exc, route, "searchWorkOrderGrouped");
    return rejectWithValue(exc);
  }
});

searchWorkOrderGrouped.query = `
  
        query($openSearch: String, $severityIds: [String], $statusIds: [Int], $dateRange: DateRangeInput, $typeIds: [Int], $limit: Int, $offset: Int, $allDrivers: Boolean, $driverIds: [Int],$tagIds: [Int], $timezone: String, $customerIds: [Int], $type: String, $sort: [WorkOrderSortGroupInput], $forceRefreshMemcached: Boolean) {
          data: searchWorkOrderGrouped(
            openSearch: $openSearch
            severityIds: $severityIds
            statusIds: $statusIds
            dateRange: $dateRange
            typeIds: $typeIds
            limit: $limit
            offset: $offset
            allDrivers: $allDrivers
            driverIds: $driverIds
            tagIds: $tagIds
            timezone: $timezone
            customerIds: $customerIds
            type: $type
            sort: $sort
            forceRefreshMemcached: $forceRefreshMemcached
          ) {
            total
            items {
              recordType
              driverId
              dynamicFormAnswerId
              days
              workOrderId
              id
              name
              unitId
              unitName
              avatarFileId
              fileKey
              repositoryPath
              scheduledDate
              customerName
              contactName
              contactEmail
              statusId
              contactPhone
              statusName
              statusColor
              durationInMinutes
              workOrderNumber
              severity
              description
              workOrderTypeId
              workOrderTypeName
              workOrderAddress
              latitude
              longitude
              statusUpdatedOn
            }
          }
        }
`;

//Slice
const workOrdersTrackerTabRedux = createSlice({
  name: "workOrders/trackerTab",
  initialState: INIT_STATE,
  reducers: {
    reset(state, action) {
      return action.payload ? { ...state, ...action.payload } : INIT_STATE;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetALL, () => INIT_STATE);

    //manageSearchWorkOrders
    builder.addCase(manageSearchWorkOrders.pending, (state) => {
      state.loadingWorkOrders = true;
    });
    builder.addCase(manageSearchWorkOrders.fulfilled, (state, action) => {
      state.loadingWorkOrders = false;
      state.workOrders = action.payload.items;
      state.totalWorkOrders = action.payload.total;
    });
    builder.addCase(manageSearchWorkOrders.rejected, (state, action) => {
      state.loadingWorkOrders = false;
      state.error = action.payload;
    });

    //updateWorkOrderMainItem
    builder.addCase(updateWorkOrderMainItem.pending, (state) => { });

    builder.addCase(updateWorkOrderMainItem.fulfilled, (state, action) => {
      state.workOrdersGrouped = action.payload.items;
      state.totalWorkOrdersGrouped = action.payload.total;
    });

    builder.addCase(updateWorkOrderMainItem.rejected, (state, action) => { });

    //searchWorkOrderGrouped
    builder.addCase(searchWorkOrderGrouped.pending, (state) => {
      state.loadingWorkOrdersGrouped = true;
    });
    builder.addCase(searchWorkOrderGrouped.fulfilled, (state, action) => {
      state.loadingWorkOrdersGrouped = false;
      state.workOrdersGrouped = action.payload.items;
      state.totalWorkOrdersGrouped = action.payload.total;
    });
    builder.addCase(searchWorkOrderGrouped.rejected, (state, action) => {
      state.loadingWorkOrdersGrouped = false;
      state.error = action.payload;
    });
  },
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = workOrdersTrackerTabRedux;

// Extract and export each action creator by name
export const { reset } = actions;

// Export the reducer, either as a default or named export
export default reducer;
